/**
 * Who has the order, and taking it over (PHA-03 / D-07).
 *
 * Unclaimed → Claim. Claimed by someone else → "Ask to take over": they are notified and can keep
 * the order until the deadline, after which it is yours. Yours with a request waiting → keep it,
 * or hand it over now. Only the claimant sees the workflow steps; the API refuses anyone else.
 * Countdowns run from the server's `seconds_left`; at zero the order is fetched again.
 * The claimant can also release the order (after a confirmation) until it is final, but not while
 * their own price edit is open: the server keeps that edit running on a release, so the patient
 * couldn't pay until its window lapsed (a takeover hand-over does close it).
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { listCatalogueStatuses } from "../../api/catalogue";
import { ApiError, describeError } from "../../api/errors";
import {
  claimOrder,
  rejectTakeover,
  releaseClaim,
  requestTakeover,
  withdrawTakeover,
  type PharmacistOrder,
} from "../../api/orders";
import { Modal } from "../../components/ui/Modal";

// After these the screen no longer matches the server: fetch again to show who has the order now.
const STALE = new Set([
  "ORDER_FINAL",
  "ALREADY_CLAIMED",
  "ALREADY_ASSIGNEE",
  "NOT_ASSIGNEE",
  "NOT_CLAIMED",
  "TAKEOVER_PENDING",
  "TAKEOVER_WINDOW_CLOSED",
]);

export function ClaimPanel({
  order,
  userId,
  fetchedAt,
  onChange,
}: {
  order: PharmacistOrder;
  userId: string | null;
  /** When `order` was fetched (React Query's dataUpdatedAt) — the countdown's reference. */
  fetchedAt: number;
  onChange: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [confirmingRelease, setConfirmingRelease] = useState(false);
  // Final statuses come from the status master (admins may change them); until it loads, Claim
  // shows and the server has the last word.
  const { data: statuses = [] } = useQuery({ queryKey: ["catalogue-statuses"], queryFn: listCatalogueStatuses, staleTime: 10 * 60_000 });
  const isFinal = statuses.some((st) => st.code === order.status_code && st.is_final);
  const takeover = order.claim_takeover ?? null;
  const left = useSecondsLeft(takeover?.seconds_left ?? null, fetchedAt, onChange);

  const action = (fn: (id: string) => Promise<unknown>) => ({
    mutationFn: () => fn(order.id),
    onMutate: () => setError(null),
    onSuccess: onChange,
    onError: (e: unknown) => {
      setError(describeError(e));
      if (e instanceof ApiError && STALE.has(e.code)) onChange();
    },
  });
  const claim = useMutation(action(claimOrder));
  const ask = useMutation(action(requestTakeover));
  const keep = useMutation(action(rejectTakeover));
  const handOver = useMutation(action(releaseClaim));
  const withdraw = useMutation(action(withdrawTakeover));
  const release = useMutation({
    mutationFn: () => releaseClaim(order.id),
    onMutate: () => setError(null),
    onSuccess: () => {
      setConfirmingRelease(false);
      onChange();
      toast.success("Claim released — the order is back in the unassigned queue.");
    },
    onError: (e: unknown) => {
      setError(describeError(e));
      // The order changed meanwhile (taken over, finished): close the dialog, show it as it is now.
      if (e instanceof ApiError && STALE.has(e.code)) {
        setConfirmingRelease(false);
        onChange();
      }
    },
  });
  const busy =
    claim.isPending || ask.isPending || keep.isPending || handOver.isPending || withdraw.isPending || release.isPending;

  const assignee = order.assignee_pharmacist_id;
  const mine = assignee !== null && assignee === userId;
  const holder = order.assignee_name ?? "Another pharmacist";
  const requester = takeover?.requested_by_name ?? "Another pharmacist";
  const countdown = left === null ? "" : formatSeconds(left);
  // The server sends `price_revision` only while the edit is running.
  const myEditOpen = !!order.price_revision && order.price_revision.started_by_user_id === userId;
  const releaseDialog = confirmingRelease && mine && !takeover && !isFinal; // shows its own error

  let content: React.ReactNode = null;
  if (assignee === null && isFinal) {
    content = null; // a finished order has nothing to claim (PHA-14; the server refuses it)
  } else if (assignee === null) {
    content = (
      <Card title="Claim this order">
        <Button onClick={() => claim.mutate()} disabled={busy} primary>
          {claim.isPending ? "Claiming…" : "Claim"}
        </Button>
      </Card>
    );
  } else if (mine && takeover) {
    content = (
      <Banner tone="amber">
        <p>
          <strong>{requester}</strong> has asked to take over this order. It passes to them in{" "}
          <strong className="tabular-nums">{countdown}</strong> unless you keep it.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <Button onClick={() => keep.mutate()} disabled={busy} primary>
            {keep.isPending ? "Keeping…" : "Keep this order"}
          </Button>
          <Button onClick={() => handOver.mutate()} disabled={busy}>
            {handOver.isPending ? "Handing over…" : "Hand over now"}
          </Button>
        </div>
      </Banner>
    );
  } else if (mine && !isFinal) {
    // Give the order up so another pharmacist can claim it (PHA-21, D-23: only "hand over" existed,
    // when someone asked for it).
    content = (
      <Banner tone="gray">
        <p>You've claimed this order.</p>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
          <Button
            onClick={() => {
              setError(null);
              setConfirmingRelease(true);
            }}
            disabled={busy || myEditOpen}
            testId="release-claim"
          >
            Release claim
          </Button>
          {myEditOpen && <span className="text-gray-500">Publish or cancel your price edit first.</span>}
        </div>
        <Modal open={confirmingRelease} title="Release claim" onClose={() => !release.isPending && setConfirmingRelease(false)}>
          <div className="space-y-4 text-sm text-gray-600">
            <p>Give {order.order_id} back to the unassigned queue? Another pharmacist can then claim it.</p>
            {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-red-700">{error}</div>}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => release.mutate()}
                disabled={release.isPending}
                data-testid="confirm-release-claim"
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {release.isPending ? "Releasing…" : "Release"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingRelease(false)}
                disabled={release.isPending}
                className="rounded-lg border px-4 py-2 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      </Banner>
    );
  } else if (!mine && takeover && takeover.requested_by_user_id === userId) {
    content = (
      <Banner tone="blue">
        <p>
          You've asked <strong>{holder}</strong> for this order. It passes to you in{" "}
          <strong className="tabular-nums">{countdown}</strong> unless they keep it.
        </p>
        <div className="mt-2">
          <Button onClick={() => withdraw.mutate()} disabled={busy}>
            {withdraw.isPending ? "Withdrawing…" : "Withdraw request"}
          </Button>
        </div>
      </Banner>
    );
  } else if (!mine) {
    content = (
      <Banner tone="gray">
        <p>
          Claimed by <strong>{holder}</strong>.{" "}
          {takeover
            ? `${requester} has asked to take it over.`
            : "If they're unavailable, ask to take it over: they're notified and can keep it for a few minutes, then it's yours."}
        </p>
        {!takeover && (
          <div className="mt-2">
            <Button onClick={() => ask.mutate()} disabled={busy}>
              {ask.isPending ? "Asking…" : "Ask to take over"}
            </Button>
          </div>
        )}
      </Banner>
    );
  }

  if (!content && !error) return null;
  return (
    <div className="space-y-2">
      {content}
      {error && !releaseDialog && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
    </div>
  );
}

/** Seconds left from the server's figure at `since`, ticking each second; `onDone` runs once at 0. */
function useSecondsLeft(serverSeconds: number | null, since: number, onDone: () => void): number | null {
  const [now, setNow] = useState(() => Date.now());
  const onDoneRef = useRef(onDone);
  useEffect(() => {
    onDoneRef.current = onDone;
  });
  useEffect(() => {
    if (serverSeconds === null) return;
    const end = since + serverSeconds * 1000;
    const timer = setInterval(() => {
      const t = Date.now();
      setNow(t);
      if (t >= end) {
        clearInterval(timer);
        onDoneRef.current();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [serverSeconds, since]);
  if (serverSeconds === null) return null;
  return Math.max(0, Math.ceil((since + serverSeconds * 1000 - now) / 1000));
}

function formatSeconds(total: number): string {
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border bg-white p-5 shadow-xs">
      <h3 className="mb-3 font-medium">{title}</h3>
      {children}
    </div>
  );
}

const TONES = {
  amber: "border-amber-200 bg-amber-50 text-amber-900",
  blue: "border-sky-200 bg-sky-50 text-sky-900",
  gray: "border-gray-200 bg-gray-50 text-gray-700",
};

function Banner({ tone, children }: { tone: keyof typeof TONES; children: React.ReactNode }) {
  return <div className={`rounded-xl border p-4 text-sm ${TONES[tone]}`}>{children}</div>;
}

function Button({
  onClick,
  disabled,
  primary,
  testId,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
  testId?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50 ${
        primary ? "bg-brand text-brand-fg" : "border bg-white text-gray-700"
      }`}
    >
      {children}
    </button>
  );
}
